import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_ICON } from "@/lib/brand";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user !== true) navigate("/admin/leads", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4" data-testid="admin-login-page">
      <div className="absolute top-6 left-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          data-testid="back-to-site-link"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
      </div>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl shadow-sky-900/5 border border-slate-100 p-8 sm:p-10">
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_ICON} alt="" className="w-14 h-14 rounded-full ring-2 ring-sky-100" />
          <h1 className="mt-5 text-2xl font-display font-bold text-slate-900">Admin Sign In</h1>
          <p className="mt-2 text-sm text-slate-600">Access the Leads dashboard.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              data-testid="admin-login-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white font-semibold px-8 py-3 rounded-full transition-all shadow-lg shadow-sky-500/30"
            data-testid="admin-login-button"
          >
            <Lock size={16} weight="bold" />
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-xs text-slate-500 text-center leading-relaxed">
          Change credentials in <code className="bg-slate-100 px-1 py-0.5 rounded">backend/.env</code> (ADMIN_EMAIL /
          ADMIN_PASSWORD), then restart the backend.
        </p>
      </div>
    </div>
  );
}
